import { useLayoutEffect, useRef } from "react";

interface CountdownDigitsProps {
  totalSeconds: number;
}

// Cross-fade between digits, in seconds.
const FADE_S = 0.04;

/**
 * Opacity keyframes over the whole countdown for the digit shown in second
 * `index`: hidden, then up for its own second, then hidden again — except the
 * last digit, which stays up if whatever follows runs late.
 */
function buildDigitKeyframes(index: number, totalSeconds: number): Keyframe[] {
  const fade = FADE_S / totalSeconds;
  const start = index / totalSeconds;
  const end = (index + 1) / totalSeconds;
  const isLast = index === totalSeconds - 1;
  return [
    { opacity: 0, offset: 0 },
    { opacity: 0, offset: start },
    { opacity: 1, offset: start + fade },
    ...(isLast
      ? [{ opacity: 1, offset: 1 }]
      : [
          { opacity: 1, offset: end - fade },
          { opacity: 0, offset: end },
          { opacity: 0, offset: 1 },
        ]),
  ];
}

/**
 * Counts totalSeconds down to 1 without React: one span per digit in a single
 * grid cell, each with one opacity animation over the whole countdown. Safari
 * needs the main thread to start or end an animation, so none does so midway.
 */
const CountdownDigits = ({ totalSeconds }: CountdownDigitsProps) => {
  const digitRefs = useRef<(HTMLSpanElement | null)[]>([]);
  const digits = Array.from(
    { length: totalSeconds },
    (_, index) => totalSeconds - index,
  );

  // Layout effect, so the animations are in place before the first paint.
  useLayoutEffect(() => {
    const animations = digitRefs.current
      .slice(0, totalSeconds)
      .map((element, index) =>
        element?.animate?.(buildDigitKeyframes(index, totalSeconds), {
          duration: totalSeconds * 1000,
          easing: "linear",
          fill: "forwards",
        }),
      );
    return () => animations.forEach((animation) => animation?.cancel());
  }, [totalSeconds]);

  return (
    <>
      <span className="sr-only">{totalSeconds}</span>
      <span aria-hidden="true" className="inline-grid">
        {digits.map((digit, index) => (
          <span
            key={digit}
            ref={(element) => {
              digitRefs.current[index] = element;
            }}
            data-countdown-digit={digit}
            className="opacity-0 [grid-area:1/1]"
          >
            {digit}
          </span>
        ))}
      </span>
    </>
  );
};

export default CountdownDigits;
